import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';

// The gallery a save-intent report is attributed to. The three fields travel
// as a unit (see useImageIntentFriction's resolveGallery): passing this object
// in a context tells the hook "this image's gallery is known", and a null
// triple (NO_IMAGE_INTENT_GALLERY) means "no gallery" rather than "use the
// Redux one". The server records a gallery only when `galleryKey` proves it.
export type ImageIntentGallery = Required<
  Pick<ImageIntentContext, 'galleryKey' | 'picsGalleryId' | 'dp2GalleryId'>
>;

export const NO_IMAGE_INTENT_GALLERY: ImageIntentGallery = Object.freeze({
  galleryKey: null,
  picsGalleryId: null,
  dp2GalleryId: null,
});

// Pooled images from another gallery carry their own gallery here, so a
// surface that mixes galleries (multi-config's price-list-matched poses) can
// attribute each image to the gallery it came from.
export const IMAGE_INTENT_GALLERY_FIELD = 'imageIntentGallery';

type AdditionalDataLike = {
  picsGalleryId?: number | string | null;
  picsGalleryPassword?: string | null;
  galleryThatInitiatedOrder?: unknown;
} | null;

interface DashboardGalleryLike {
  id?: number | string | null;
  pics_gallery_id?: number | string | null;
  password?: string | null;
}

// The gallery an order's additionalData was built for: a Pics gallery, or
// else the DP2 gallery that started the order. Same rule as the hook's Redux
// defaults, applied to a snapshot (a cart order's own additionalData).
export const imageIntentGalleryFromAdditionalData = (
  additionalData: AdditionalDataLike | undefined,
): ImageIntentGallery => {
  const data = additionalData ?? {};
  const picsGalleryId = data.picsGalleryId ?? null;
  const initiatingGallery = data.galleryThatInitiatedOrder as
    | { galleryId?: number | string | null }
    | null
    | undefined;

  return {
    galleryKey: data.picsGalleryPassword ?? null,
    picsGalleryId,
    dp2GalleryId:
      picsGalleryId == null ? initiatingGallery?.galleryId ?? null : null,
  };
};

// A gallery object from the parent dashboard payload. `id` is the DP2 gallery
// id when the gallery is not a Pics gallery (the same rule
// buildGalleryOrderContext uses for galleryThatInitiatedOrder).
export const imageIntentGalleryFromDashboardGallery = (
  gallery: DashboardGalleryLike | null | undefined,
): ImageIntentGallery => {
  const picsGalleryId = gallery?.pics_gallery_id ?? null;

  return {
    galleryKey: gallery?.password ?? null,
    picsGalleryId,
    dp2GalleryId: picsGalleryId == null ? gallery?.id ?? null : null,
  };
};

export const tagWithImageIntentGallery = <T extends object>(
  images: T[],
  gallery: ImageIntentGallery,
): Array<T & { [IMAGE_INTENT_GALLERY_FIELD]: ImageIntentGallery }> =>
  images.map((image) => ({ ...image, [IMAGE_INTENT_GALLERY_FIELD]: gallery }));

// The gallery a pooled image was tagged with, or an empty object so that
// spreading it into a context leaves the hook's defaults in charge.
export const imageIntentGalleryOf = (
  image: unknown,
): ImageIntentGallery | Record<string, never> => {
  const tagged =
    image && typeof image === 'object'
      ? (image as Record<string, unknown>)[IMAGE_INTENT_GALLERY_FIELD]
      : undefined;

  return tagged && typeof tagged === 'object'
    ? (tagged as ImageIntentGallery)
    : {};
};
